/**
 * O formulário de nova tarefa (H10).
 *
 * ── O que ele substitui ───────────────────────────────────────────────────
 *
 * Havia dois botões "Nova tarefa" — no Cliente 360 e no atendimento — e
 * nenhum abria formulário: os dois mandavam um título fixo ("Retornar para
 * Fulano"), sem prazo, sem responsável e sem corpo. O vendedor não conseguia
 * dizer QUANDO nem O QUÊ, que é a metade da tarefa que importa.
 *
 * ── Criar A PARTIR DO CONTEXTO, com o vínculo preenchido ──────────────────
 *
 * A Técnica da H10 é explícita: *"criar tarefa solta e depois vincular é o
 * caminho que ninguém completa"*. Por isso o vínculo entra por `alvo` e é
 * mostrado, não editável: quem abriu o formulário de dentro da ficha de um
 * cliente já disse de quem é a tarefa, e oferecer um seletor de cliente ali
 * seria pedir de novo o que a tela inteira já respondeu.
 *
 * Aberto da tela de Tarefas não há contexto, e aí a tarefa nasce solta — que é
 * legítimo (a API aceita) e é a única forma de criar um lembrete que não é de
 * ninguém em particular.
 *
 * ── O prazo é `datetime-local`, e vai em UTC ──────────────────────────────
 *
 * O campo trabalha na hora LOCAL de quem digita, sem fuso; o contrato quer um
 * instante. A conversão é uma linha, e errá-la desloca o prazo em três horas —
 * o suficiente para uma tarefa marcada para as 9h da manhã nascer atrasada.
 */

import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AlvoDaTarefa, PrioridadeDaTarefa } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useApi, useResponsaveisDeTarefa } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, CampoArea, CampoSelecao, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { PRIORIDADES } from '@/entities/comercial/tarefa';
import './nova-tarefa.css';

/** Nova chave por comando: repetir a MESMA devolveria a resposta anterior (B30). */
const novaChave = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `cmd-${Date.now()}-${Math.random()}`;

const MOTIVOS: Record<string, string> = {
  sem_cadastro_no_crm: 'está com o cadastro incompleto',
  vinculo_suspenso: 'vínculo suspenso',
  sem_acesso_ao_atendimento: 'sem acesso ao atendimento',
};

export interface VinculoDaTarefa {
  readonly alvo: AlvoDaTarefa;
  /** Como chamar o vínculo na tela — o nome do cliente, o título do negócio. */
  readonly rotulo: string;
}

export function NovaTarefa({
  vinculo,
  aoFechar,
  aoCriar,
  /** De onde o formulário foi aberto. Vira o `motivo` da origem, e fica na trilha. */
  deOnde,
}: {
  vinculo?: VinculoDaTarefa | undefined;
  aoFechar: () => void;
  /**
   * Chamado depois de criar, ANTES de fechar.
   *
   * O diálogo some no sucesso, e sumir sem dizer nada deixa quem clicou sem
   * saber se funcionou. Quem abriu é que sabe onde pôr a confirmação — e o que
   * mais precisa recarregar além da lista de tarefas.
   *
   * Recebe a `referencia_externa` da tarefa criada, e não o id canônico: quem
   * espera a projeção precisa casar com o que a lista traz, e a lista traz
   * tarefa nascida no próprio motor, ainda sem cunhagem. Quem só quer avisar
   * que deu certo ignora o argumento.
   */
  aoCriar?: ((referenciaExterna: string) => void) | undefined;
  deOnde: string;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();

  const [titulo, definirTitulo] = useState('');
  const [corpo, definirCorpo] = useState('');
  const [prazo, definirPrazo] = useState('');
  const [prioridade, definirPrioridade] = useState<PrioridadeDaTarefa>('normal');
  const [responsavel, definirResponsavel] = useState('');

  const equipe = useResponsaveisDeTarefa();

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      // Devolve a tarefa criada: quem abriu o formulário precisa da referência
      // dela para esperar a projeção alcançá-la.
      return api.criarTarefa({
        corpo: {
          titulo: titulo.trim(),
          // Vazio vira ausente, e não string vazia: o contrato aceita as duas,
          // e uma descrição em branco na lista parece descrição perdida.
          corpo: corpo.trim() === '' ? null : corpo.trim(),
          prazo_em: emInstante(prazo),
          prioridade,
          ...(responsavel === '' ? {} : { responsavel_referencia_externa: responsavel }),
          ...(vinculo ? { alvo: vinculo.alvo } : {}),
          origem: { tipo: 'manual', motivo: deOnde },
        },
        idempotencyKey: novaChave(),
      });
    },
    onSuccess: (criada) => {
      /*
       * A lista de tarefas E a ficha do cliente.
       *
       * A contagem da aba "Tarefas" no Cliente 360 vem da consulta do 360, e
       * não da lista — invalidar só a lista deixava o número velho na tela até
       * a pessoa sair do menu e voltar. Foi exatamente o que aconteceu: a
       * tarefa era criada, e o "Tarefas 2" continuava dizendo 1.
       *
       * Vale a pena revalidar: o bloco de tarefas do 360 consulta o Twenty ao
       * vivo (por `taskTargets`), e não a projeção — então a tarefa recém-criada
       * já está lá, sem esperar o webhook chegar.
       */
      invalidar([
        chaves.tarefas(),
        ...(vinculo?.alvo.tipo === 'cliente' ? [chaves.cliente360(vinculo.alvo.id)] : []),
      ]);
      aoCriar?.(criada.referencia_externa);
      aoFechar();
    },
  });

  const podeSalvar = titulo.trim() !== '' && !salvar.isPending;

  return (
    <Dialogo
      titulo="Nova tarefa"
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            desabilitado={!podeSalvar}
            motivo={titulo.trim() === '' ? 'Escreva o que precisa ser feito.' : 'Criando…'}
            onClick={() => salvar.mutate()}
          >
            {salvar.isPending ? 'Criando…' : 'Criar tarefa'}
          </Botao>
        </>
      }
    >
      {salvar.error != null && (
        <Faixa tom="bloqueado">
          {recadoDeErro(salvar.error, { generica: 'Não foi possível criar a tarefa.' })}
        </Faixa>
      )}

      {vinculo && (
        /*
         * O vínculo é MOSTRADO, e não escolhido.
         *
         * Quem abriu isto de dentro da ficha de um cliente já disse de quem é a
         * tarefa. Um seletor aqui pediria de novo o que a tela inteira já
         * respondeu — e seria o campo onde se erra, porque o nome certo e o
         * nome parecido ficam um do lado do outro numa lista.
         */
        <p className="nova-tarefa__vinculo">
          Sobre <strong>{vinculo.rotulo}</strong>
        </p>
      )}

      <Campo
        rotulo="O que precisa ser feito"
        value={titulo}
        maxLength={500}
        autoFocus
        onChange={(e) => definirTitulo(e.target.value)}
      />

      <CampoArea
        rotulo="Detalhes"
        ajuda="Opcional. O que quem for fazer precisa saber e não cabe no título."
        value={corpo}
        rows={3}
        maxLength={20_000}
        onChange={(e) => definirCorpo(e.target.value)}
      />

      <div className="nova-tarefa__linha">
        <Campo
          rotulo="Prazo"
          type="datetime-local"
          ajuda="Opcional. Sem prazo, a tarefa nunca conta como atrasada."
          value={prazo}
          onChange={(e) => definirPrazo(e.target.value)}
        />

        <CampoSelecao
          rotulo="Prioridade"
          value={prioridade}
          onChange={(e) => definirPrioridade(e.target.value as PrioridadeDaTarefa)}
        >
          {PRIORIDADES.map(({ valor, nome }) => (
            <option key={valor} value={valor}>
              {nome}
            </option>
          ))}
        </CampoSelecao>
      </div>

      <CampoSelecao
        rotulo="Responsável"
        ajuda="Em branco, a tarefa fica com você."
        value={responsavel}
        onChange={(e) => definirResponsavel(e.target.value)}
      >
        <option value="">Eu mesmo</option>
        {(equipe.data?.itens ?? [])
          // Quem pede já é a opção em branco acima; repeti-lo faria a mesma
          // escolha existir duas vezes com nomes diferentes.
          .filter((pessoa) => !pessoa.sou_eu)
          .map((pessoa) => (
            <option
              key={pessoa.referencia_externa ?? pessoa.nome}
              value={pessoa.referencia_externa ?? ''}
              disabled={!pessoa.pode_receber}
            >
              {pessoa.nome}
              {pessoa.motivo ? ` — ${MOTIVOS[pessoa.motivo] ?? pessoa.motivo}` : ''}
            </option>
          ))}
      </CampoSelecao>
    </Dialogo>
  );
}

/**
 * `datetime-local` → instante.
 *
 * O valor do campo é hora LOCAL sem fuso (`2026-09-10T09:00`). `new Date` o lê
 * como local — que é o que se quer — e `toISOString` o entrega em UTC, que é o
 * que o contrato pede. Mandar a string crua faria o servidor lê-la como UTC, e
 * uma tarefa marcada para as 9h nasceria com prazo às 6h em São Paulo.
 */
function emInstante(valor: string): string | null {
  if (valor === '') return null;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data.toISOString();
}
